import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import './SiteEditor.scss';

// What "هوية الموقع" (Branding.js) and "محتوى الصفحة الرئيسية" (Gym/SiteContent.js) share: both write words that the
// teacher's theme shows, so both open on the theme itself -- its picture, its colours and the sections its home
// page draws -- and both mark every one of their own sections with whether this theme shows it. Read-only: the
// theme is chosen on "شكل الموقع" (SiteLook.js), nothing here saves it.

/** The home-page blocks a theme can list (docs/themes/THEME-SPEC.md), as the teacher would call them. */
export const BLOCK_NAMES = {
    hero: 'أول الشاشة', heroCta: 'أول الشاشة', trustStats: 'الأرقام', featureGrid: 'المميزات', programCards: 'الكورسات',
    pricingPlans: 'الاشتراكات', teamGrid: 'الفريق', coachBio: 'عن المدرس', testimonials: 'آراء الطلاب', faq: 'أسئلة شائعة',
    appBadges: 'تطبيق الموبايل', ctaBand: 'الشريط الختامي', subjectFeature: 'قسم التخصص', steps: 'إزاي تبدأ',
    liveSessions: 'الحصص المباشرة', freeLessons: 'دروس مجانية', honorBoard: 'لوحة الشرف',
};

/** The specialty section's kinds (layout.subject), named as on "شكل الموقع". */
export const SUBJECT_NAMES = {
    problem: 'مسألة الأسبوع', sim: 'تجربة بمحاكاة', periodic: 'جدول دوري', body: 'أجهزة الجسم', mcq: 'بنك أسئلة',
    tools: 'برامج هندسية', verse: 'بيت الأسبوع', levels: 'مستويات اللغة', clubs: 'نوادي محادثة', projects: 'مشاريع برمجة',
    gallery: 'معرض أعمال', tracks: 'تراكات تدريب', juz: 'متابعة الحفظ', timeline: 'خط زمني', cases: 'حالات عملية',
    roadmap: 'خريطة مهنية', plans: 'خطط اشتراك', meals: 'يوم صحي', journey: 'رحلة نجوم', features: 'مميزات المنصة',
    dashboard: 'لوحة الطالب', certificate: 'شهادة إتمام', calendar: 'تقويم الأسبوع', compare: 'قبل وبعد',
};

const FONT_WEIGHTS = {
    Cairo: '400;700', Tajawal: '400;700', Almarai: '400;700', 'IBM Plex Sans Arabic': '400;700', 'Readex Pro': '400;700',
    'Noto Kufi Arabic': '400;700', Changa: '400;700', 'El Messiri': '400;700', Amiri: '400;700', 'Reem Kufi': '400;700',
    'Baloo Bhaijaan 2': '400;700', Lalezar: null, Inter: '400;700', Poppins: '400;700', 'Baloo 2': '400;700',
    'DM Sans': '400;700', 'Playfair Display': '400;700',
};

export function loadFont(name) {
    if (!name || !(name in FONT_WEIGHTS)) return;
    const id = 'font-' + name.replace(/\s+/g, '-');
    if (document.getElementById(id)) return;
    const w = FONT_WEIGHTS[name];
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + name.replace(/ /g, '+') + (w ? ':wght@' + w : '') + '&display=swap';
    document.head.appendChild(link);
}

/** The teacher's theme as these pages need it, from the branding /teachers/me returns. `home` is null for a theme
 *  that draws the classic home page (the older themes list no blocks). */
export function themeOf(branding) {
    const b = branding || {};
    let def = null;
    try { def = b.themeDefinition ? JSON.parse(b.themeDefinition) : null; } catch (e) { def = null; }
    const d = def || {};
    const preset = (d.presets && d.presets[0]) || {};
    const home = Array.isArray(d.home) && d.home.length ? d.home : null;
    const layout = d.layout || {};
    return {
        id: b.theme || d.id || null,
        name: (d.name && (d.name.ar || d.name.en)) || b.theme || 'الثيم الافتراضي',
        home,
        layout,
        subject: layout.subject && layout.subject !== 'none' ? layout.subject : null,
        colors: { primary: b.primaryColor || preset.primary || '#198baf', accent: b.accentColor || preset.accent || '#eeb807', bg: b.bgColor || preset.background || '#ffffff' },
        fonts: { head: b.fontHeading || (d.fonts && d.fonts.heading) || 'Cairo', body: b.fontBody || (d.fonts && d.fonts.body) || 'Cairo' },
    };
}

/** Whether the theme's home page shows one of the given blocks (the classic page shows none of them). */
export const shows = (theme, ...blocks) => !!(theme.home && blocks.some(b => theme.home.indexOf(b) >= 0));

/** The student site's address: a verified own domain, else the platform subdomain. */
export function siteOf(me, info) {
    if (me && me.customDomain && me.customDomainStatus === 'VERIFIED') return me.customDomain;
    if (me && me.subdomain && info && info.baseDomain) return `${me.subdomain}.${info.baseDomain}`;
    return null;
}

export function siteUrl(site) {
    if (window.location.hostname === 'localhost') return `http://localhost:3200/?host=${encodeURIComponent(site)}`;
    return `https://${site}/`;
}

/** The page's opening card: the theme's picture and colours, the sections its home page draws in order (the ones
 *  written on this page lit), and the ways to the look, the site and the sister page. */
export class ThemeBar extends Component {
    componentDidMount() {
        const { theme } = this.props;
        if (theme) { loadFont(theme.fonts.head); loadFont(theme.fonts.body); }
    }

    render() {
        const { theme, site, here, editable = [] } = this.props;
        if (!theme) return null;
        const c = theme.colors;
        const other = here === 'content'
            ? { to: '/settings/branding', label: 'هوية الموقع', icon: 'pi pi-id-card' }
            : { to: '/settings/home-content', label: 'محتوى الصفحة الرئيسية', icon: 'pi pi-pencil' };
        const seen = {};
        const map = (theme.home || []).filter(b => BLOCK_NAMES[b] && !seen[BLOCK_NAMES[b]] && (seen[BLOCK_NAMES[b]] = true));
        return (
            <div className="se-theme" style={{ '--sp': c.primary, '--sa': c.accent, '--sb': c.bg, '--sf': `'${theme.fonts.head}'` }}>
                <div className="se-theme-shot">
                    {theme.id && <img src={`/theme-previews/${theme.id}.jpg`} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />}
                    <span className="se-theme-live"><i></i>على موقعك دلوقتي</span>
                </div>
                <div className="se-theme-body">
                    <small className="se-theme-kicker">ثيم موقعك</small>
                    <div className="se-theme-name">
                        <b>{theme.name}</b>
                        <span className="se-swatch" aria-hidden="true"><i style={{ background: c.primary }}></i><i style={{ background: c.accent }}></i><i style={{ background: c.bg }}></i></span>
                    </div>
                    {theme.home ? (
                        <>
                            <p>صفحتك الرئيسية بالثيم ده فيها الأقسام دي بالترتيب{editable.length ? '، والمنوّر منها بتكتبه من الصفحة دي:' : ':'}</p>
                            <ol className="se-map">
                                {map.map((b, i) => (
                                    <li key={b} className={editable.indexOf(b) >= 0 ? 'is-here' : ''} style={{ '--i': i }}>
                                        <span>{i + 1}</span>{b === 'subjectFeature' && theme.subject ? SUBJECT_NAMES[theme.subject] || BLOCK_NAMES[b] : BLOCK_NAMES[b]}
                                    </li>
                                ))}
                            </ol>
                        </>
                    ) : (
                        <p>ده ثيم كلاسيكي: صفحته الرئيسية ثابتة (أول الشاشة، الكورسات، قسم الميزة). الثيمات الجديدة بتضيف أقسام زي الأرقام وآراء الطلاب وإزاي تبدأ.</p>
                    )}
                    <div className="se-theme-actions">
                        <Link to="/settings/look" className="ui-btn is-small"><i className="pi pi-palette"></i>تغيير الثيم والألوان</Link>
                        {site && (
                            <a href={siteUrl(site)} target="_blank" rel="noopener noreferrer" className="ui-btn is-small is-secondary">
                                <i className="pi pi-external-link"></i>افتح موقعي
                            </a>
                        )}
                        <Link to={other.to} className="se-theme-other"><i className={other.icon}></i>{other.label}</Link>
                    </div>
                </div>
            </div>
        );
    }
}

/** What one section's chip says: shown by this theme, shown with the theme's own words until filled, or not shown. */
export const STATE = {
    on: { cls: 'is-ok', label: 'ظاهر في ثيمك' },
    auto: { cls: 'is-wait', label: 'فاضي — بيظهر كلام جاهز' },
    hidden: { cls: 'is-wait', label: 'فاضي — مش هيظهر' },
    off: { cls: 'is-off', label: 'مش في ثيمك الحالي' },
};

export const StateChip = ({ state }) => {
    const s = STATE[state] || STATE.off;
    return <span className={`ui-chip ${s.cls}`}><span className="dot"></span>{s.label}</span>;
};
